import { PickPartial } from '@avalenti89/typescript-utils';
import { SignJWT } from 'jose';

export type ErrorData = {
	error: Error | any;
	payload?: any;
	meta?: any;
	tstamp?: string;
};

export type SendErrorParams = {
	baseUrl: string;
	privateKey: Uint8Array;
};

const hexEncode = (value: string) => {
	return Array.from({ length: value.length }).reduce<string>(
		(prev, curr, i) => {
			const hex = value.charCodeAt(i).toString(16);
			return prev + hex;
			// return prev + `000${hex}`.slice(-4);
		},
		'',
	);
};

const postError = (baseUrl: string, body: string) => {
	if (!baseUrl.includes('http')) throw Error('logerror - Wrong baseUrl');
	fetch(new URL('/logerror', baseUrl).href, {
		method: 'post',
		headers: {
			'Content-Type': 'text/plain',
		},
		body,
		credentials: 'include',
	}).catch(console.error);
};

/**
 * Sanitizes data by removing non-serializable objects (Event, BigInt,
 * circular references, Dates) so the JWT payload can be JSON-stringified.
 */
const sanitizeData = (obj: any, seen: WeakSet<object> = new WeakSet()): any => {
	if (obj === null || typeof obj !== 'object') {
		return typeof obj === 'bigint' ? obj.toString() : obj;
	}

	// Check if it's an Event or similar non-cloneable object
	if (obj instanceof Event) {
		return `[Event: ${obj.type}]`;
	}

	if (obj instanceof Date) {
		return obj.toISOString();
	}

	// Circular reference guard
	if (seen.has(obj)) {
		return '[Circular]';
	}
	seen.add(obj);

	// Handle arrays
	if (Array.isArray(obj)) {
		return obj.map((value) => sanitizeData(value, seen));
	}

	// Handle plain objects
	const sanitized: any = {};
	for (const key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			const value = obj[key];
			if (value instanceof Event) {
				sanitized[key] = `[Event: ${value.type}]`;
			} else if (typeof value === 'bigint') {
				sanitized[key] = value.toString();
			} else if (typeof value === 'object' && value !== null) {
				sanitized[key] = sanitizeData(value, seen);
			} else {
				sanitized[key] = value;
			}
		}
	}
	return sanitized;
};

export const getPrivateKey = (key: string = hexEncode('missing')) => {
	if (key.length < 32) key = key.padEnd(32, '0');
	if (key.length > 32) key = key.slice(0, 32);
	return new Uint8Array(Math.ceil(key.length / 2)).map((_, i) => {
		const index = i * 2;
		return parseInt(key[index] + key[index + 1], 16);
	});
};

/**
 * Signs the error payload as a JWT and POSTs it to `/logerror`.
 *
 * @param namespace - Logical source of the error (app/module id).
 * @param data - Error, optional payload, and optional meta to include in the JWT.
 * @param sendErrorParams - API base URL and HMAC key used to sign the JWT.
 *
 * Side effects: logs sign failures to the console; POSTs a JWT body to `/logerror`.
 * `/logerror` accepts only JWTs. If signing the full payload fails, a minimal JWT
 * (namespace + sign error) is signed instead; `postError` is invoked once with the
 * resulting token. POST failures are not retried with a new JWT.
 */
export const sendError = (
	namespace: string,
	{ error, payload, meta }: ErrorData,
	{ baseUrl, privateKey }: SendErrorParams,
) => {
	const body = {
		error: String(error),
		payload: sanitizeData(payload),
		namespace,
		meta: sanitizeData(meta),
	};

	new SignJWT(body)
		.setProtectedHeader({ alg: 'HS256' })
		.sign(privateKey)
		.catch((signError) => {
			// Sign failure: emit a minimal JWT, then POST once.
			console.error(signError);
			return new SignJWT({
				error: String(signError),
				namespace,
				meta: { cause: 'logerror sign failed' },
			})
				.setProtectedHeader({ alg: 'HS256' })
				.sign(privateKey);
		})
		.then((jwt) => postError(baseUrl, jwt))
		.catch(console.error);
};

export const logError = (
	namespace: string,
	data: ErrorData,
	sendErrorParams?: PickPartial<SendErrorParams, 'privateKey'>,
) => {
	const { error, payload, meta } = data;
	const tstamp = new Date().toISOString();
	console.groupCollapsed(`%c${namespace}`, 'color:red');
	console.log('tstamp', tstamp);
	console.log('payload', payload);
	console.log('meta', meta);
	console.error(error);
	console.groupEnd();
	if (sendErrorParams) {
		sendError(
			namespace,
			{ tstamp, ...data },
			{
				privateKey: getPrivateKey(),
				...sendErrorParams,
			},
		);
	}
};
